// @flow
import React, { useRef } from 'react'
import { Text, Transformer } from 'react-konva'
import Image from './Image'
import ErrorBoundary from './ErrorBoundary'

import type { DesignBoardItem } from '../types'
import { colors } from '../styles'

type Props = {
  item: DesignBoardItem,
  editable: boolean,
  isSelected: boolean,
  onSelect: Function,
  onChange: Function,
}

export const TEXT_PADDING = 1

export const Sample = ({ item, editable, isSelected, onSelect, onChange }: Props) => {
  // console.log('Sample', item, isSelected)
  const contentRef = useRef()
  const trRef = useRef()

  const { image, text } = item

  window.curNode = contentRef

  React.useEffect(() => {
    // console.log('updating sample', isSelected, trRef.current?.nodes)
    if (editable && isSelected && contentRef?.current) {
      // console.log('actually updating', contentRef, contentRef.current)
      // we need to attach transformer manually
      trRef.current.nodes([ contentRef.current ])
      trRef.current.getLayer().batchDraw()
    }
  }, [ editable, trRef, isSelected ])

  const commonAttrs = editable ? {
    onClick: onSelect,
    onTap: onSelect,
    draggable: editable,
    onDragEnd: (e) => {
      if (!editable) { return }
      // console.log('onDragEnd', e)
      onChange({
        ...item.attributes,
        x: e.target.x(),
        y: e.target.y(),
      })
    },
    ...item.attributes
  } : {
    draggable: false,
    ...item.attributes
  }

  const handleImageTransform = (e) => {
    if (!editable) { return }
    console.log('handleImageTransform', e.evt.detail, e)
  }

  const handleImageTransformEnd = (e) => {
    if (!editable) { return }
    // console.log('handleImageTransformEnd', e.evt.detail, e)
    // transformer is changing scale of the node
    // and NOT its width or height
    // but in the store we have only width and height
    // to match the data better we will reset scale on transform end
    const node = contentRef.current
    const scaleX = node.scaleX()
    const scaleY = node.scaleY()
    const rotation = node.rotation()

    // console.log('node', scaleX, scaleY, rotation, node.className, node)
    // we will reset it back
    node.scaleX(1)
    node.scaleY(1)

    onChange({
      ...item.attributes,
      x: node.x(),
      y: node.y(),
      // set minimal value
      width: node.width() * scaleX,
      height: node.height() * scaleY,
      rotation,
    })
  }

  const handleTextTransformEnd = (e) => {
    if (!editable) { return }
    console.log('handleTextTransformEnd', e.evt.detail, e)
    // transformer is changing scale of the node
    // and NOT its width or height
    // but in the store we have only width and height
    // to match the data better we will reset scale on transform end
    const node = contentRef.current
    const scaleX = node.scaleX()
    const scaleY = node.scaleY()

    // console.log('node', scaleX, scaleY, node.className, node)
    // we will reset it back
    node.scaleX(1)
    node.scaleY(1)

    // reset since it seems to get cut off if too narrow
    node.text(text.label)

    onChange({
      ...item.attributes,
      x: node.x(),
      y: node.y(),
      // set minimal value
      width: node.width() * scaleX,
      // height: node.height() * scaleY
      //height: 36
    })
  }

  const handleTextTransform = (e) => {
    // console.log('handleTextTransform', e)
    // contentRef.current.scaleX(1)
    // contentRef.current.scaleY(1)
    contentRef.current.setAttrs({
      width: contentRef.current.width() * contentRef.current.scaleX(),
      scaleX: 1
    })
  }

  const handleTextEdit = (e) => {
    // console.log('handleTextEdit', e)
    e.cancelBubble = true

    // hide text node and transformer:
    const textNode = contentRef.current
    const tr = trRef.current
    const stage = textNode.getStage()

    textNode.hide()
    tr.hide()

    // create textarea over canvas with absolute position
    // first we need to find position for textarea
    // how to find it?

    // at first lets find position of text node relative to the stage:
    const textPosition = textNode.absolutePosition()

    // so position of textarea will be the sum of positions above:
    const areaPosition = {
      x: stage.container().offsetLeft + textPosition.x,
      y: stage.container().offsetTop + textPosition.y,
    }

    // create textarea and style it
    const textarea = document.createElement('textarea')
    document.querySelector('.konvajs-content').appendChild(textarea)

    // apply many styles to match text on canvas as close as possible
    // remember that text rendering on canvas and on the textarea can be different
    // and sometimes it is hard to make it 100% the same. But we will try...
    textarea.value = textNode.text()
    textarea.style.position = 'absolute'
    textarea.style.top = areaPosition.y + 'px'
    textarea.style.left = areaPosition.x + 'px'
    textarea.style.width = textNode.width() - textNode.padding() * 2 + 'px'
    textarea.style.height = textNode.height() - textNode.padding() * 2 + 5 + 'px'
    textarea.style.fontSize = textNode.fontSize() + 'px'
    textarea.style.fontWeight = 500
    textarea.style.border = `1px solid ${colors.darkOrange}`
    textarea.style.padding = '0px'
    textarea.style.margin = '0px'
    textarea.style.overflow = 'hidden'
    textarea.style.background = 'none'
    textarea.style.outline = 'none'
    textarea.style.resize = 'none'
    textarea.style.lineHeight = textNode.lineHeight()
    textarea.style.fontFamily = textNode.fontFamily()
    textarea.style.transformOrigin = 'left top'
    textarea.style.textAlign = textNode.align()
    textarea.style.color = textNode.fill()
    const rotation = textNode.rotation()
    let transform = ''
    if (rotation) {
      transform += 'rotateZ(' + rotation + 'deg)'
    }

    let px = 0
    // also we need to slightly move textarea on firefox
    // because it jumps a bit
    const isFirefox = navigator.userAgent.toLowerCase().indexOf('firefox') > -1
    if (isFirefox) {
      px += 2 + Math.round(textNode.fontSize() / 20)
    }
    transform += 'translateY(-' + px + 'px)'

    textarea.style.transform = transform

    // reset height
    textarea.style.height = 'auto'
    // after browsers resized it we can set actual value
    textarea.style.height = `${textarea.scrollHeight + 3}px`

    textarea.focus()

    function removeTextarea () {
      textarea.parentNode.removeChild(textarea)
      window.removeEventListener('click', handleOutsideClick)
      textNode.show()
      tr.show()
      // tr.forceUpdate()
    }

    function setTextareaWidth (newWidth) {
      if (!newWidth) {
        // set width for placeholder
        newWidth = textNode.placeholder.length * textNode.fontSize()
      }
      // some extra fixes on different browsers
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
      const isFirefox = navigator.userAgent.toLowerCase().indexOf('firefox') > -1
      if (isSafari || isFirefox) {
        newWidth = Math.ceil(newWidth)
      }

      const isEdge = document.documentMode || /Edge/.test(navigator.userAgent)
      if (isEdge) {
        newWidth += 1
      }
      textarea.style.width = newWidth + 'px'
    }

    textarea.addEventListener('change', function(e) {
      onChange({
        ...item.attributes,
      }, { text: { label: e.target.value } })
    })

    textarea.addEventListener('keydown', function (e) {
      // hide on enter
      // but don't hide on shift + enter
      if (e.keyCode === 13 && !e.shiftKey) {
        textNode.text(textarea.value)
        removeTextarea()
      }
      // on esc do not set value back to node
      if (e.keyCode === 27) {
        removeTextarea()
      }
    })

    textarea.addEventListener('keydown', function (e) {
      const scale = textNode.getAbsoluteScale().x
      setTextareaWidth(textNode.width() * scale)
      textarea.style.height = 'auto'
      textarea.style.height = textarea.scrollHeight + textNode.fontSize() + 'px'
    })

    function handleOutsideClick (e) {
      if (e.target !== textarea) {
        textNode.text(textarea.value)
        removeTextarea()
      }
    }

    setTimeout(() => {
      window.addEventListener('click', handleOutsideClick)
    })
  }

  const imageAttrs = {
    ...commonAttrs,
    // onTransform: handleImageTransform,
    onTransformEnd: handleImageTransformEnd,
  }

  const textAttrs = {
    ...commonAttrs,
    fontSize: 16,
    fontFamily: 'HelveticaNeue_Lt',
    // fontWeight: '500',
    strokeWidth: 0.5,
    fill: colors.midGray,
    stroke: colors.midGray,
    padding: TEXT_PADDING,
    wrap: 'word',
    onTransform: handleTextTransform,
    onTransformEnd: handleTextTransformEnd,
    onDblClick: handleTextEdit,
    onDblTap: handleTextEdit,
  }

  const anchors = text?.label ? [ 'middle-left', 'middle-right' ] : [ 'top-left', 'top-right', 'bottom-left', 'bottom-right' ]

  return (
    <>
      <ErrorBoundary>
        {image
          ? <Image ref={contentRef} url={image.imageUrl} {...imageAttrs} />
          : text
            ? <Text ref={contentRef} text={text.label} {...textAttrs} />
            : null
        }
        {editable && isSelected && (
          <Transformer
            ref={trRef}
            enabledAnchors={anchors}
            anchorStroke={colors.darkOrange}
            borderStroke={colors.darkOrange}
            rotationSnaps={[0, 45, 90, 135, 180, 225, 270, 315]}
            anchorCornerRadius={25}
            rotateAnchorOffset={25}
            boundBoxFunc={(oldBox, newBox) => {
              // console.log('box change', oldBox, newBox)
              // limit resize
              if (newBox.width < 20 || newBox.height < 20) {
                return oldBox
              }
              return newBox
            }}
          />
        )}
      </ErrorBoundary>
    </>
  )
}

Sample.defaultProps = {
  editable: false,
}

// Sample.whyDidYouRender = true

export default Sample
